/**
 * Section: Header & Navigation (custom-header.js)
 * Features:
 * - Desktop Dropdowns with Hover Intent & Grace Period (250ms)
 * - Desktop Language Dropdown Switcher
 * - Mobile Header Controls & 3-Tab Segmented Drawer (Figma Nodes 475:9980, 475:9901 & 475:10020)
 * - Smart Scroll-Up Announcement Bar
 */

(function () {
  'use strict';

  function initCustomHeader() {
    const headerNav = document.querySelector('.juli-header-nav');
    const pillGroup = document.querySelector('.juli-nav-pill-group');
    const navItems = document.querySelectorAll('.juli-nav-item');
    const toggle = document.querySelector('.juli-mobile-toggle');
    const mobileBackdrop = document.querySelector('.juli-mobile-backdrop');
    const mobileModal = document.querySelector('.juli-mobile-modal');
    const headerWrapper = document.querySelector('.juli-header-wrapper');
    const announcementBar = document.querySelector('.juli-announcement-bar');
    const langDropdown = document.querySelector('.juli-lang-dropdown-wrapper');
    const langBtn = document.querySelector('.juli-lang-btn');
    const mobileTabs = document.querySelectorAll('.juli-mobile-tab');
    const mobilePanels = document.querySelectorAll('.juli-mobile-panel');

    // Clean up any previously mutated inline styles
    if (pillGroup) pillGroup.removeAttribute('style');

    // Ensure site-loaded class is present when page has no hero cinematic preloader (e.g. subpages like revenue-audit)
    if (!document.querySelector('.juli-hero-section')) {
      document.body.classList.add('juli-site-loaded');
    }

    // 1. Desktop & Mobile Smooth Anchor Links & Dropdowns
    const navLinks = document.querySelectorAll('.juli-nav-link');
    const dropdownPanels = document.querySelectorAll('.juli-nav-panel');
    let dropdownCloseTimeout = null;

    function openNavDropdown(key) {
      clearTimeout(dropdownCloseTimeout);
      if (!pillGroup) return;
      pillGroup.classList.add('has-open-dropdown');

      navLinks.forEach((link) => {
        const isMatch = link.getAttribute('data-nav-dropdown') === key;
        link.classList.toggle('is-active', isMatch);
      });

      dropdownPanels.forEach((panel) => {
        const isMatch = panel.getAttribute('data-panel-key') === key;
        panel.classList.toggle('is-open', isMatch);
      });
    }

    function closeNavDropdown() {
      dropdownCloseTimeout = setTimeout(() => {
        if (!pillGroup) return;
        pillGroup.classList.remove('has-open-dropdown');
        navLinks.forEach((link) => link.classList.remove('is-active'));
        dropdownPanels.forEach((panel) => panel.classList.remove('is-open'));
      }, 160);
    }

    navLinks.forEach((link) => {
      const dropdownKey = link.getAttribute('data-nav-dropdown');

      if (dropdownKey) {
        link.addEventListener('mouseenter', () => {
          if (window.innerWidth > 900) {
            openNavDropdown(dropdownKey);
          }
        });

        link.addEventListener('click', (e) => {
          if (window.innerWidth > 900) {
            e.preventDefault();
            const panel = document.querySelector(`.juli-nav-panel[data-panel-key="${dropdownKey}"]`);
            if (panel && panel.classList.contains('is-open')) {
              closeNavDropdown();
            } else {
              openNavDropdown(dropdownKey);
            }
          }
        });
      }

      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#') && href.length > 1) {
          const targetEl = document.querySelector(href);
          if (targetEl) {
            e.preventDefault();
            targetEl.scrollIntoView({ behavior: 'smooth' });
          } else {
            // Anchor is not on current subpage: redirect to homepage with anchor
            e.preventDefault();
            window.location.href = '/' + href;
          }
        }
      });
    });

    if (pillGroup) {
      pillGroup.addEventListener('mouseleave', () => {
        if (window.innerWidth > 900) {
          closeNavDropdown();
        }
      });
      pillGroup.addEventListener('mouseenter', () => {
        clearTimeout(dropdownCloseTimeout);
      });
    }

    // 3. Mobile Hamburger & Overlay Toggle
    function closeMobileMenu() {
      if (!headerNav) return;
      headerNav.classList.remove('is-mobile-open');
      document.body.classList.remove('juli-mobile-menu-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    function openMobileMenu() {
      if (!headerNav) return;
      hideAnnouncement();
      headerNav.classList.add('is-mobile-open');
      document.body.classList.add('juli-mobile-menu-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    }

    if (toggle && headerNav) {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = headerNav.classList.contains('is-mobile-open');
        if (isOpen) {
          closeMobileMenu();
        } else {
          openMobileMenu();
        }
      });
    }

    const drawerCloseBtns = document.querySelectorAll('.juli-mobile-close-btn');
    drawerCloseBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeMobileMenu();
      });
    });

    if (mobileBackdrop) {
      mobileBackdrop.addEventListener('click', () => {
        closeMobileMenu();
      });
    }

    // 4. Mobile 3-Tab Segmented Capsule Switcher (Figma 475:9917)
    mobileTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = tab.getAttribute('data-tab');

        mobileTabs.forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });

        mobilePanels.forEach((panel) => {
          const panelType = panel.getAttribute('data-panel');
          panel.classList.toggle('is-active', panelType === targetTab);
        });
      });
    });

    // Close mobile menu when clicking internal links or CTA
    const mobileLinks = document.querySelectorAll('.juli-mobile-modal a, .juli-mobile-modal-cta');
    mobileLinks.forEach((link) => {
      link.addEventListener('click', () => {
        closeMobileMenu();
      });
    });

    // Close mobile menu / active desktop menus on resize to desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth > 900) {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        navItems.forEach((item) => item.classList.remove('is-open'));
        if (langDropdown) langDropdown.classList.remove('is-open');
        closeMobileMenu();
      }
    });

    // Close open dropdowns on outside click or ESC key
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.juli-nav-pill-group')) {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        navItems.forEach((item) => item.classList.remove('is-open'));
      }
      if (langDropdown && !e.target.closest('.juli-lang-dropdown-wrapper')) {
        langDropdown.classList.remove('is-open');
      }
      if (headerNav && !e.target.closest('.juli-header-nav') && !e.target.closest('.juli-mobile-overlay')) {
        closeMobileMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        navItems.forEach((item) => item.classList.remove('is-open'));
        if (langDropdown) langDropdown.classList.remove('is-open');
        closeMobileMenu();
      }
    });

    // 5. Pull-Down To Reveal Announcement Bar
    // Starts HIDDEN on first reload. Only appears when user intentionally scrolls/pulls UP at the very top (scrollY <= 5).
    // Tucks away as soon as scrolled down. NEVER appears inside dropdown or when mobile menu is open.
    let isVisible = false;
    let touchStartY = 0;

    function showAnnouncement() {
      if (document.body.classList.contains('juli-mobile-menu-open') || (headerNav && headerNav.classList.contains('is-mobile-open'))) {
        return;
      }
      if (announcementBar && headerWrapper && !isVisible) {
        isVisible = true;
        announcementBar.classList.add('is-visible');
        headerWrapper.classList.add('has-announcement-visible');
      }
    }

    function hideAnnouncement() {
      if (announcementBar && headerWrapper && isVisible) {
        isVisible = false;
        announcementBar.classList.remove('is-visible');
        headerWrapper.classList.remove('has-announcement-visible');
      }
    }

    if (announcementBar && headerWrapper) {
      // Wheel / Trackpad Scroll Up at top
      function handleWheel(e) {
        if (document.body.classList.contains('juli-mobile-menu-open')) return;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;
        if (currentScrollY <= 5 && e.deltaY < -3) {
          showAnnouncement();
        } else if (e.deltaY > 5 || currentScrollY > 15) {
          hideAnnouncement();
        }
      }

      // Touch pull-down on mobile at top
      function handleTouchStart(e) {
        if (document.body.classList.contains('juli-mobile-menu-open')) return;
        if (e.touches && e.touches.length > 0) {
          touchStartY = e.touches[0].clientY;
        }
      }

      function handleTouchMove(e) {
        if (document.body.classList.contains('juli-mobile-menu-open')) return;
        if (!e.touches || e.touches.length === 0) return;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;
        const currentY = e.touches[0].clientY;
        const touchDelta = currentY - touchStartY;

        // Pull down gesture while at top
        if (currentScrollY <= 5 && touchDelta > 20) {
          showAnnouncement();
        } else if (touchDelta < -10 || currentScrollY > 15) {
          hideAnnouncement();
        }
      }

      // Scroll listener: toggle announcement bar and is-scrolled state when header touches section below hero
      const navContainer = document.querySelector('.juli-header-nav-container');
      function handleScroll() {
        if (document.body.classList.contains('juli-mobile-menu-open')) return;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;
        if (currentScrollY > 15) {
          hideAnnouncement();
        }

        const heroSection = document.querySelector('.juli-hero-section') || document.querySelector('[class*="hero"]');
        const headerH = navContainer ? navContainer.offsetHeight : 70;

        let isScrolled = false;
        const isRevenueAuditPage =
          document.body.classList.contains('template-suffix-revenue-audit') ||
          !!document.querySelector('.juli-audit-wrapper') ||
          window.location.pathname.includes('revenue-audit');

        if (isRevenueAuditPage) {
          isScrolled = true;
        } else if (heroSection) {
          const heroRect = heroSection.getBoundingClientRect();
          // Exact trigger: when bottom of hero reaches or passes behind the sticky header
          isScrolled = heroRect.bottom <= headerH;
        } else {
          isScrolled = currentScrollY > 300;
        }

        if (navContainer) {
          navContainer.classList.toggle('is-scrolled', isScrolled);
        }
        if (headerNav) {
          headerNav.classList.toggle('is-scrolled', isScrolled);
        }
      }

      // Check on initial load in case page is already scrolled
      handleScroll();

      // Auto-reveal smoothly 5 seconds AFTER the intro video completes
      let announcementScheduled = false;
      function scheduleAnnouncementAfterIntro() {
        if (announcementScheduled) return;
        announcementScheduled = true;

        setTimeout(() => {
          const currentScrollY = window.scrollY || window.pageYOffset || 0;
          if (currentScrollY <= 20) {
            showAnnouncement();
          }
        }, 5000);
      }

      if (document.body.classList.contains('juli-site-loaded')) {
        scheduleAnnouncementAfterIntro();
      } else {
        window.addEventListener('juli:intro-completed', scheduleAnnouncementAfterIntro, { once: true });
        // Fallback in case video sequence is delayed
        setTimeout(scheduleAnnouncementAfterIntro, 12000);
      }

      // Skip hero cinematic video preloader when user switches language
      document.addEventListener('click', (e) => {
        const langTarget = e.target.closest('.juli-desktop-lang-opt, .juli-mobile-lang-opt, .juli-mobile-lang-btn, [name="locale_code"]');
        if (langTarget) {
          try {
            sessionStorage.setItem('juli_skip_hero_preloader', 'true');
          } catch (err) {}
        }
      });

      document.addEventListener('submit', (e) => {
        if (e.target && e.target.matches('form[action*="localization"], #HeaderLanguageFormDesktop, #HeaderLanguageFormMobile, #HeaderLanguageFormModal')) {
          try {
            sessionStorage.setItem('juli_skip_hero_preloader', 'true');
          } catch (err) {}
        }
      });

      window.addEventListener('wheel', handleWheel, { passive: true });
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomHeader);
  } else {
    initCustomHeader();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.id && e.target.id.includes('custom_header')) {
      initCustomHeader();
    }
  });
})();
